import {
  factorMaps as staticFactorMaps,
  municipalityMaps as staticMunicipalityMaps,
  provinceMaps as staticProvinceMaps,
  samplingMap as staticSamplingMap,
} from '../data/suitabilityMaps';

function normalizeText(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function normalizeCategory(value) {
  const normalized = normalizeText(value).toLowerCase();

  if (normalized === 'bohol' || normalized === 'province') return 'province';
  if (normalized === 'requirements' || normalized === 'factor') return 'factor';
  if (normalized === 'sampling_site' || normalized === 'sampling site' || normalized === 'sampling') return 'sampling';
  if (normalized === 'municipality') return 'municipality';

  return normalized || 'municipality';
}

function toDataUrl(base64, mimeType) {
  const cleanBase64 = normalizeText(base64);
  if (!cleanBase64) return '';
  const resolvedMime = normalizeText(mimeType) || 'image/jpeg';
  return `data:${resolvedMime};base64,${cleanBase64}`;
}

function mapStaticEntry(entry, overrides = {}) {
  return {
    id: overrides.id ?? null,
    key: normalizeText(overrides.key ?? entry.key),
    title: normalizeText(overrides.title ?? entry.title),
    subtitle: normalizeText(overrides.subtitle ?? entry.subtitle),
    municipality: normalizeText(overrides.municipality ?? entry.municipality),
    description: normalizeText(overrides.description ?? entry.description),
    assetPath: normalizeText(overrides.assetPath ?? entry.assetPath),
    rawAssetPath: normalizeText(overrides.rawAssetPath ?? entry.assetPath),
    imageBase64: normalizeText(overrides.imageBase64),
    imageMimeType: normalizeText(overrides.imageMimeType) || 'image/jpeg',
    category: normalizeText(overrides.category ?? entry.category).toLowerCase() || 'municipality',
    aliases: Array.isArray(overrides.aliases) ? overrides.aliases : (entry.aliases || []),
    sortOrder: Number(overrides.sortOrder ?? entry.sortOrder ?? 0) || 0,
    isActive: overrides.isActive ?? true,
  };
}

function normalizeDynamicRecord(record = {}) {
  return {
    id: record.id ?? null,
    category: normalizeCategory(record.category),
    name: normalizeText(record.municipality),
    imageBase64: normalizeText(record.image_base64 ?? record.map_image_base64),
    imageMimeType: normalizeText(record.image_mime_type) || 'image/jpeg',
  };
}

function buildDynamicLookup(records = []) {
  const lookup = new Map();

  records
    .map(normalizeDynamicRecord)
    .filter((row) => row.category && row.name)
    .forEach((row) => {
      lookup.set(`${row.category}::${row.name.toLowerCase()}`, row);
    });

  return lookup;
}

function mapWithDynamicImage(entry, category, dynamicLookup, identityValue) {
  const identity = normalizeText(identityValue);
  const dynamic = dynamicLookup.get(`${category}::${identity.toLowerCase()}`);
  
  let dataUrl = '';
  if (dynamic?.imageBase64) {
    dataUrl = toDataUrl(dynamic.imageBase64, dynamic.imageMimeType);
  } else if (dynamic?.id && dynamic?.category) {
    const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3000').replace(/\/+$/, '');
    // Append a timestamp so that when the catalog is reloaded after saving, 
    // the browser cache is bypassed and the new image is fetched.
    dataUrl = `${API_BASE_URL}/maps/catalog/${encodeURIComponent(dynamic.category)}/${encodeURIComponent(dynamic.id)}/image?t=${Date.now()}`;
  }

  return mapStaticEntry(entry, {
    id: dynamic?.id ?? null,
    category,
    assetPath: dataUrl || entry.assetPath,
    rawAssetPath: '',
    imageBase64: dynamic?.imageBase64 ?? '',
    imageMimeType: dynamic?.imageMimeType ?? 'image/jpeg',
  });
}

function buildMunicipalityLookup(municipalityMaps) {
  return municipalityMaps.reduce((lookup, map) => {
    lookup[map.municipality] = map;
    lookup[map.key] = map;
    (map.aliases || []).forEach((alias) => {
      lookup[alias] = map;
    });
    return lookup;
  }, {});
}

function sortByName(a, b) {
  return normalizeText(a.municipality || a.title || a.key).localeCompare(
    normalizeText(b.municipality || b.title || b.key)
  );
}

export function normalizeMapRecord(record = {}) {
  const row = normalizeDynamicRecord(record);

  return {
    id: row.id,
    key: row.name || 'map-record',
    title: row.name || 'Map',
    subtitle: row.name || '',
    municipality: row.name,
    description: '',
    assetPath: toDataUrl(row.imageBase64, row.imageMimeType),
    rawAssetPath: '',
    imageBase64: row.imageBase64,
    imageMimeType: row.imageMimeType,
    category: row.category || 'municipality',
    aliases: [],
    sortOrder: 0,
    isActive: true,
  };
}

const defaultProvinceMaps = staticProvinceMaps.map((entry) =>
  mapStaticEntry(entry, {
    category: 'province',
    assetPath: '',
    rawAssetPath: '',
    imageBase64: '',
  })
);

const defaultFactorMaps = staticFactorMaps.map((entry) =>
  mapStaticEntry(entry, {
    category: 'factor',
    assetPath: '',
    rawAssetPath: '',
    imageBase64: '',
  })
);

const defaultSamplingMap = mapStaticEntry(staticSamplingMap, {
  category: 'sampling',
  assetPath: '',
  rawAssetPath: '',
  imageBase64: '',
});

const defaultMunicipalityMaps = staticMunicipalityMaps.map((entry) =>
  mapStaticEntry(entry, {
    category: 'municipality',
    assetPath: '',
    rawAssetPath: '',
    imageBase64: '',
  })
);

export const defaultMapLibrary = {
  provinceMaps: defaultProvinceMaps,
  factorMaps: defaultFactorMaps,
  samplingMap: defaultSamplingMap,
  municipalityMaps: defaultMunicipalityMaps,
  municipalityLookup: buildMunicipalityLookup(defaultMunicipalityMaps),
  suitabilityCollections: {
    province: defaultProvinceMaps,
    factor: defaultFactorMaps,
    sampling: defaultSamplingMap ? [defaultSamplingMap] : [],
    municipality: defaultMunicipalityMaps,
  },
};

export function buildMapLibrary(records = []) {
  const dynamicLookup = buildDynamicLookup(Array.isArray(records) ? records : []);

  const municipalityMaps = staticMunicipalityMaps
    .map((entry) => mapWithDynamicImage(entry, 'municipality', dynamicLookup, entry.municipality))
    .sort(sortByName);

  const provinceMaps = staticProvinceMaps.map((entry) =>
    mapWithDynamicImage(entry, 'province', dynamicLookup, entry.title || entry.key)
  );

  const factorMaps = staticFactorMaps.map((entry) =>
    mapWithDynamicImage(entry, 'factor', dynamicLookup, entry.title || entry.key)
  );

  const samplingMap = mapWithDynamicImage(
    staticSamplingMap,
    'sampling',
    dynamicLookup,
    staticSamplingMap.title || staticSamplingMap.key
  );

  return {
    provinceMaps,
    factorMaps,
    samplingMap,
    municipalityMaps,
    municipalityLookup: buildMunicipalityLookup(municipalityMaps),
    suitabilityCollections: {
      province: provinceMaps,
      factor: factorMaps,
      sampling: samplingMap ? [samplingMap] : [],
      municipality: municipalityMaps,
    },
  };
}
